import React, { useEffect, useState } from 'react'
import "./contact.scss"
import { Button, Form, Input, message, Upload, Modal } from 'antd'
import { UploadOutlined, FilePdfOutlined, DeleteOutlined } from '@ant-design/icons'
import { useTranslation } from "react-i18next"
import { AppDispatch, RootState } from '../../store'
import { useDispatch, useSelector } from 'react-redux'
import { sendDemandContact, idledemandContactStatus } from './redux/demandContactSlice'
import PageHeader from '../common/pageHeader/PageHeader'

const ContactSection: React.FC = () => {
  const { t } = useTranslation('contact')
  const name = t("Your name")
  const email = t("Email")
  const phone = t("Phone number")
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [successModalVisible, setSuccessModalVisible] = useState(false)
  const dispatch = useDispatch<AppDispatch>()
  const [form] = Form.useForm()
  const demandContactStatus = useSelector(
    (state: RootState) => state.demandContact.demandContactStatus,
  )

  useEffect(() => {
    switch (demandContactStatus) {
      case 'success':
        message.success(t('Your demand of contact has been successfully sent.'))
        setSuccessModalVisible(true)
        setSelectedFile(null)
        setSubmitting(false)
        form.resetFields()
        break
      case 'error':
        message.error(t('An error occurred.', { ns: 'common' }))
        setSubmitting(false)
        break
    }

    return () => {
      dispatch(idledemandContactStatus())
    }
  }, [demandContactStatus, dispatch, form, t])

  const handleContactSubmit = async (values: { name: string; email: string; phone: string; message: string, website: string; }) => {
    if (submitting) return
    setSubmitting(true)

    const trimmedEmail = values.email.trim()
    const formData = {
      name: values.name,
      email: trimmedEmail,
      phone: values.phone,
      website: "Château Le Corvier",
      message: values.message,
      file: selectedFile ? [selectedFile] : [],
    }
    dispatch(sendDemandContact(formData))
  }

  const uploadProps = {
    beforeUpload: (file: any) => {
      if (file.type === 'application/pdf') {
        setSelectedFile(file)
      } else {
        message.error(t('NOT_PDF', { filename: file.name }))
      }
      return false
    },
    showUploadList: false,
  }

  return (
    <section className="contact-page" id="contact">
      <PageHeader
        label={t('get in touch', { ns: 'common' }) as string}
        title={t('A question? A reservation?') as string}
        subtitle={t("Don't hesitate to write to us, we'll answer within a few days.") as string}
      />

      <div className="contact__body">
        <div className="container">
          <div className="contact__grid">
            <div className="contact__info">
              <h3>{t('Practical information')}</h3>

              <div className="contact__info-item">
                <p className="contact__info-label">{t('Address')}</p>
                <p className="contact__info-value">Corvier, 41600 Vouzon<br />Sologne, Val de Loire</p>
              </div>
              <div className="contact__info-item">
                <p className="contact__info-label">{t('Phone', { ns: 'common' })}</p>
                <p className="contact__info-value">06 23 21 21 89<br />06 65 78 27 76</p>
              </div>
              <div className="contact__info-item">
                <p className="contact__info-label">{t('Email')}</p>
                <p className="contact__info-value">contact@cosynest-re.fr<br />roudautaulac@gmail.com</p>
              </div>
              <div className="contact__info-item">
                <p className="contact__info-label">{t('Access')}</p>
                <p className="contact__info-value">{t('2h from Paris via the A71')}<br />{t('Close to Lamotte-Beuvron')}</p>
              </div>

              <div className="contact__social">
                <a href="https://www.facebook.com/dianescozyhouse" target="_blank" rel="noopener noreferrer">Facebook</a>
                <a href="https://www.tiktok.com/@chateau.du.corvier2023" target="_blank" rel="noopener noreferrer">TikTok</a>
                <a href="https://aucoeurservice.fr" target="_blank" rel="noopener noreferrer">AuCoeur Service</a>
              </div>
            </div>

            <Form
              form={form}
              className="contact__form"
              onFinish={handleContactSubmit}
              autoComplete='off'
              layout='vertical'
              disabled={submitting}
            >
              <div className="form-row">
                <Form.Item name="name" label={t('Your name')} rules={[{ required: true, message: t('Please input your name') as string }]}>
                  <Input placeholder={name} disabled={submitting} />
                </Form.Item>
                <Form.Item
                  name="email"
                  label={t('Email')}
                  rules={[
                    { required: true, message: t('Please let us contact you by your email') as string },
                    { type: 'email', message: t('Please enter a valid email address', { ns: 'common' }) as string, validateTrigger: 'onSubmit' }
                  ]}
                >
                  <Input placeholder={email} disabled={submitting} />
                </Form.Item>
              </div>
              <Form.Item name="phone" label={t('Phone number')}>
                <Input placeholder={phone} disabled={submitting} />
              </Form.Item>
              <Form.Item
                name="message"
                label={t('Message', { ns: 'common' })}
                rules={[{ required: true, message: t('Please input your message') as string}]}
              >
                <Input.TextArea rows={6} disabled={submitting} />
              </Form.Item>

              <Form.Item
                name="file"
              >
                <div className={`contact-upload-zone ${selectedFile ? 'has-file' : ''}`}>
                  {!selectedFile ? (
                    <Upload {...uploadProps}>
                      <div className="upload-zone-content">
                        <UploadOutlined className="upload-icon" />
                        <p className="upload-text">{t('Click or drag PDF file here to upload')}</p>
                        <p className="upload-hint">{t('Attach a PDF document (Optional)')}</p>
                      </div>
                    </Upload>
                  ) : selectedFile ? (
                    <div className="uploaded-file-display">
                      <FilePdfOutlined className="file-icon" />
                      <span className="file-name">{selectedFile.name}</span>
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => setSelectedFile(null)}
                        className="remove-file-btn"
                        disabled={submitting}
                      />
                    </div>
                  ) : null}
                </div>
              </Form.Item>

              <button type="submit" className="btn-submit" disabled={submitting || demandContactStatus === 'loading'}>
                {t('Submit Request')}
              </button>
            </Form>
          </div>
        </div>
      </div>

      <Modal
        open={successModalVisible}
        onOk={() => setSuccessModalVisible(false)}
        onCancel={() => setSuccessModalVisible(false)}
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <p>{t('Your request has been submitted successfully, we received your email and we\'ll answer you within a few days', { ns: 'common' })}</p>
      </Modal>
    </section>
  )
}

export default ContactSection
